import * as React from 'react';
import Oidc from 'oidc-client';
import 'src/core/assets/loader.css';
import HttpService from './http.service';
import autobind from 'autobind-decorator';
import 'src/core/utils/linq';
import { UAParser } from 'ua-parser-js';

Oidc.Log.logger = console;
Oidc.Log.level = Oidc.Log.NONE;
(window as any)['logger'] = Oidc.Log;

interface SecureContentProps {
    config: AuthenticationConfig;
    children?: React.ReactNode;
    authenticatingView?: React.ReactNode;
    onAuthentication?: (result: AuthenticationResult) => void;
}

interface SecureContentStatus {
    authenticating: boolean,
    result: AuthenticationResult | undefined,
    error: string | undefined
}

function AuthenticatingView(props: any) {
    return <div className="loaderContainer">
        <div className="loader">
            <div className="circle">&nbsp;</div>
            <div className="circle">&nbsp;</div>
            <div className="circle">&nbsp;</div>
            <div className="circle">&nbsp;</div>
        </div>
    </div>;
}

interface AuthenticationConfig {
    clientId: string,
    authority?: string,
    resource?: string,
    redirectUri?: string,
    silentRedirectUri?: string,
    passthrough?: string, 
    extraQueryParameter?: string
    postChangeOrganizationRedirectUri?: string,
}

export interface AuthenticationResult {
    idToken: string | undefined;
    accessToken: string | undefined;
    firstName: string | undefined;
    lastName: string | undefined;
    email: string | undefined;
    roles: string[] | undefined;
    scopes: string[] | undefined;
    groups: string[] | undefined;
    profile: any | undefined;
    error: string | undefined;
    logout: () => Promise<any>;
    changeOrganization: (nextOrganizationId:string, redirectUri?:string) => Promise<any>;
}

function ensureArray(item: any): any {
    if (Array.isArray(item))
        return item;
    if (!item)
        return [];
    return [item];
}

export class AuthenticationService {
    private config: AuthenticationConfig;
    private _identityToken: undefined | string;
    private _accessToken: undefined | string;
    private _user: undefined | string;
    private _authenticationContext: Oidc.UserManager | undefined = undefined;
    public static language: string = localStorage.getItem('spt_lang') || 'en';
    public onUserLoaded?: (result: AuthenticationResult) => void;

    private static readonly MAX_SILENT_RENEW_RETRIES = 3;
    private _silentRenewRetryCount = 0;
    private _silentRenewRetryTimer: number | undefined = undefined;
    private _resumeRenewalCheckRegistered = false;

    public get identityToken(): string | undefined {
        return this._identityToken;
    }

    public get accessToken(): string | undefined {
        return this._accessToken;
    }

    public get user(): string | undefined {
        return this._user;
    }

    private get context() {
        this._identityToken = undefined;
        
        this.loginAsync = this.loginAsync.bind(this);
        this.logout = this.logout.bind(this);
        this.processResult = this.processResult.bind(this);

        return this._authenticationContext = new Oidc.UserManager({
            automaticSilentRenew: true,
            authority: this.config.authority,
            client_id: this.config.clientId,
            redirect_uri: this.config.redirectUri,
            response_type: "id_token token",
            scope: `${this.config.resource}`,
            post_logout_redirect_uri: this.config.redirectUri,
            silent_redirect_uri: this.config.silentRedirectUri ?? this.config.redirectUri,
            loadUserInfo: true,
            userStore: new Oidc.WebStorageStateStore({ store: window.localStorage }),
        });
    }

    constructor(config: AuthenticationConfig) {
        this.getHumanizedDeviceInfo.bind(this);
        this.config = config;
    }

    getHumanizedDeviceInfo = () => {
        const ua = navigator.userAgent;
        const parsed = new UAParser(ua);
        const parts: string[] = [];

        const browser = parsed.getBrowser();
        const device = parsed.getDevice();
        const os = parsed.getOS();

        if (browser && (browser.name || '').length !== 0) parts.push(`${browser.name} ${browser.version}`.trim());

        if (device && (device.type || '').length !== 0) parts.push(`${device.type} ${device.model}`.trim());

        if (os && (os.name || '').length !== 0) parts.push(`${os.name} ${os.version}`.trim());

        if (parts.length === 0 || parts.every((x) => (x || '').length === 0)) return ua;

        return parts.join(' ');
    }

    public logout() {
        const self = this;
        return self.context.signoutRedirect({
            extraQueryParams: {
                'device': self.getHumanizedDeviceInfo(),
                '$language': AuthenticationService.language
            },
        });
    }

    public loginSilentAsync(): Promise<AuthenticationResult> {
        if (this.config.passthrough) {
            return Promise.resolve(this.processResult({ id_token: 'debug', access_token: 'debug', profile: this.config.passthrough }));
        }
        return new Promise((resolve, reject) => {
            var self = this;
            var mgr = this.context;
            mgr.signinSilentCallback().then(function () {
                mgr.getUser().then(function (user) {
                    if (user && !user.expired && self.scopesAreValid(user.scopes)) {
                        resolve(self.processResult(user));
                    }
                    else {
                        console.log("User not logged in");
                        mgr.signinSilent().then(user => {
                            resolve(self.processResult(user));
                        }).catch(() => {
                            console.log('sign in silent error');
                            mgr.signinRedirect({
                                redirect_uri: window.location.href,
                                extraQueryParams: {
                                    'device': self.getHumanizedDeviceInfo(),
                                    '$language': AuthenticationService.language
                                },
                            });
                        });
                    }
                });
            }).catch(function (e) {
                mgr.getUser().then(function (user) {
                    if (user && !user.expired && self.scopesAreValid(user.scopes)) {
                        resolve(self.processResult(user));
                    }
                    else {
                        console.log("User not logged in");
                        mgr.signinSilent().then(user => {
                            resolve(self.processResult(user));
                        }).catch(() => {
                            console.log('sign in silent error');
                            mgr.signinRedirect({
                                redirect_uri: window.location.href,
                                extraQueryParams: {
                                    'device': self.getHumanizedDeviceInfo(),
                                    '$language': AuthenticationService.language
                                },
                            });
                        });
                    }
                });
            });
        });
    }

    @autobind
    private scopesAreValid(userScopes: string[]): boolean {
        var requestedScopes = this.config!.resource!.split(" ");
        return requestedScopes.all(scope => userScopes.any(o => o === scope));
    }

    private handleSilentRenewError = (mgr: Oidc.UserManager, err: any) => {
        console.error('[Auth] Silent renew failed:', err?.message || err);

        if (this._silentRenewRetryTimer) return;

        this._silentRenewRetryCount++;

        if (this._silentRenewRetryCount > AuthenticationService.MAX_SILENT_RENEW_RETRIES) {
            console.error(`[Auth] Silent renew failed ${this._silentRenewRetryCount} times in a row, forcing re-authentication`);
            this._silentRenewRetryCount = 0;
            mgr.signinRedirect({
                redirect_uri: window.location.href,
                extraQueryParams: {
                    'device': this.getHumanizedDeviceInfo(),
                    '$language': AuthenticationService.language
                },
            });
            return;
        }

        const delay = Math.min(2000 * Math.pow(2, this._silentRenewRetryCount - 1), 30000);
        console.log(`[Auth] Retrying silent renew in ${delay}ms (attempt ${this._silentRenewRetryCount}/${AuthenticationService.MAX_SILENT_RENEW_RETRIES})`);

        this._silentRenewRetryTimer = window.setTimeout(() => {
            this._silentRenewRetryTimer = undefined;
            mgr.signinSilent().catch(retryErr => this.handleSilentRenewError(mgr, retryErr));
        }, delay);
    }

    private registerResumeRenewalCheck(mgr: Oidc.UserManager) {
        if (this._resumeRenewalCheckRegistered) return;
        this._resumeRenewalCheckRegistered = true;

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState !== 'visible') return;

            mgr.getUser().then(user => {
                if (!user) return;
                if (user.expired || user.expires_in <= 60) {
                    console.log('[Auth] Tab resumed with expired/expiring token, forcing silent renew');
                    mgr.signinSilent().catch(err => this.handleSilentRenewError(mgr, err));
                }
            }).catch(() => {});
        });
    }

    public loginAsync(): Promise<AuthenticationResult> {
        if (this.config.passthrough) {
            return Promise.resolve(this.processResult({ id_token: 'debug', access_token: 'debug', profile: this.config.passthrough }));
        }
        return new Promise((resolve, reject) => {
            var self = this;
            var mgr = this.context;
            mgr.events.addUserLoaded(e=> {
                if (this._user != null && e?.profile != null) {
                    const prevTenantId = (this._user as any)['tenant_id'];
                    const nextTenantId = e.profile['tenant_id'];
                    if (prevTenantId != nextTenantId)
                    {
                        console.log("=== Reload due to tenant change ===")
                        window.location.reload();
                    }
                }
                self._silentRenewRetryCount = 0;
                self.processResult(e);
            })
            mgr.events.addUserSignedOut(function () {
                mgr.removeUser().then(result=>mgr.signinSilent().then(user => {
                    resolve(self.processResult(user));
                }).catch(() => {
                    console.log('sign in silent error');
                    mgr.signinRedirect({
                        redirect_uri: window.location.href,
                        extraQueryParams: {
                            'device': self.getHumanizedDeviceInfo(),
                            '$language': AuthenticationService.language
                        },
                    });
                }));
            });
            mgr.events.addSilentRenewError(function (err) {
                self.handleSilentRenewError(mgr, err);
            });
            self.registerResumeRenewalCheck(mgr);

            mgr.signinRedirectCallback().then(function () {
                mgr.getUser().then(function (user) {
                    if (user && !user.expired && self.scopesAreValid(user.scopes)) {
                        resolve(self.processResult(user));
                    }
                    else {
                        console.log("User not logged in");
                        mgr.signinRedirect({
                            redirect_uri: window.location.href,
                            extraQueryParams: {
                                'device': self.getHumanizedDeviceInfo(),
                                '$language': AuthenticationService.language
                            },
                        });
                    }
                });
            }).catch(function (e) {
                // mgr.getUser().then(function (user) {
                //     if (user && !user.expired && self.scopesAreValid(user.scopes)) {
                //         console.log("User logged in", user.profile);
                //         resolve(self.processResult(user));
                //     }
                //     else {
                //         console.log("User not logged in");
                        mgr.signinRedirect({
                            redirect_uri: window.location.href,
                            extraQueryParams: {
                                'device': self.getHumanizedDeviceInfo(),
                                '$language': AuthenticationService.language
                            },
                        });
                //     }
                // });
            });
        });
    }

    @autobind
    public changeOrganization(nextOrganizationId:string, redirectUri?:string): Promise<void> {
        const self = this;
        return self.context.signoutRedirect({
            redirect_uri: window.location.href,
            extraQueryParams: {
                'device': self.getHumanizedDeviceInfo(),
                '$language': AuthenticationService.language,
                'changeOrganization': nextOrganizationId,
                'postOrganizationChangeRedirectUri': redirectUri ?? this.config.postChangeOrganizationRedirectUri
            },
        });
    }

    private processResult(result: any): AuthenticationResult {
        this._identityToken = result.id_token;
        this._accessToken = result.access_token;
        this._user = result.profile;
        var profile = result.profile || {}
        HttpService.accessToken = result.access_token;

        const lang = profile['default_lang'];
        if (lang != null && localStorage.getItem('spt_lang') == null) {
            AuthenticationService.language = lang;
            HttpService.language = lang;
            localStorage.setItem('spt_lang', lang);
        }

        const processedResult = {
            accessToken: result.access_token,
            idToken: result.id_token,
            profile: result.profile,
            error: undefined,
            firstName: profile["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname"] || profile["given_name"],
            lastName: profile["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname"] || profile["family_name"],
            email: profile["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"] || profile["email"],
            roles: ensureArray(profile["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"]) || ensureArray(profile["role"]),
            scopes: ensureArray(profile["scope"]),
            groups: ensureArray(profile["group"]),
            logout: this.logout,
            changeOrganization: this.changeOrganization,
        }
        this.onUserLoaded && this.onUserLoaded(processedResult)
        return processedResult;
    }
}

let authenticationService: AuthenticationService;

const AuthContext = React.createContext<AuthenticationResult>(undefined as any);
const { Provider, Consumer } = AuthContext;

class SecureContent extends React.Component<SecureContentProps, SecureContentStatus> {
    private authenticationService: AuthenticationService;

    constructor(props: SecureContentProps) {
        super(props);
        this.authenticationService = authenticationService = new AuthenticationService(props.config);
        this.authenticationService.onUserLoaded = result => {
            this.setState({ result });
        };
        this.state = {
            result: undefined,
            authenticating: true,
            error: undefined
        }
		
        this.authenticate = this.authenticate.bind(this);
    }
    
    UNSAFE_componentWillMount() {
        this.authenticate().then(result => {
            this.setState({ authenticating: false, error: result.error });
            if (this.props.onAuthentication) {
                this.props.onAuthentication(result);
            }
        }).catch(error => {
            this.setState({ error: error });
        });
    }

    private authenticate(): Promise<AuthenticationResult> {
        return new Promise<AuthenticationResult>((resolve, reject) => {
            this.authenticationService.loginAsync().then(result => {
                this.setState({ result: result });
                resolve(result);
            }).catch(error => {
                resolve({
                    idToken: undefined,
                    accessToken: undefined,
                    profile: undefined,
                    error: error,
                    email: undefined,
                    firstName: undefined,
                    lastName: undefined,
                    roles: undefined,
                    groups: undefined,
                    scopes: undefined,
                    logout: this.authenticationService.logout,
                    changeOrganization: this.authenticationService.changeOrganization
                });
            });
        });
    }

    public render() {
        if (this.state.error) {
            return <div>
                <h1>ERROR</h1>
                <p>{JSON.stringify(this.state.error)}</p>
            </div>;
        }
        return (this.state.authenticating) ? (this.props.authenticatingView || <AuthenticatingView />) : (<Provider value={this.state.result as any}>{this.props.children}</Provider> || <div></div>);
    }
}

export interface IdentityProps {
    identity: AuthenticationResult
}

export function withIdentity<T extends React.ComponentType<any>>(Component: T) {
    // ...and returns another component...
    return function ComponentBoundWithAppContext(props: any) {
        // ... and renders the wrapped component with the current context!
        // Notice that we pass through any additional props as well
        return (
            <Consumer>
                {appContext => <Component {...props} identity={appContext} />}
            </Consumer>
        );
    };
}

export const useIdentity = () => {
    const context = React.useContext(AuthContext);
    if (context === undefined) {
      throw new Error('useIdentity must be used within an AppProvider');
    }
    return context;
  };

export function isInRole(identity: AuthenticationResult, roles: string[]) {
    if (!identity || !identity.roles)
        return false;
    let exists = false;
    identity.roles.map(role => exists = exists || roles.filter(r => r === role).length > 0);
    return exists;
}

export { authenticationService };
export default SecureContent;